import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Download, TrendingUp, TrendingDown, DollarSign, AlertTriangle } from "lucide-react";
import { computeJobProfit } from "../../lib/jobCost";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function completedMs(job) {
  const d = job.workCompletedAt || job.end || job.statusChangedAt || null;
  if (!d) return null;
  const t = new Date(d).getTime();
  return isFinite(t) ? t : null;
}

export default function JobProfitabilitySection({ jobs = [], invoices = [], estimates = [], pricebook = [], customers = [], fromDate, toDate, onOpenCustomer }) {
  const [expanded, setExpanded] = useState({});

  const byId = useMemo(() => {
    const m = {};
    pricebook.forEach((p) => { m[p.id] = p; });
    return m;
  }, [pricebook]);

  const invByJob = useMemo(() => {
    const m = {};
    invoices.forEach((i) => { if (i.jobId) m[i.jobId] = i; });
    return m;
  }, [invoices]);

  const estByJob = useMemo(() => {
    const m = {};
    estimates.forEach((e) => { if (e.jobId) m[e.jobId] = e; });
    return m;
  }, [estimates]);

  const fromMs = fromDate ? new Date(`${fromDate}T00:00:00`).getTime() : null;
  const toMs = toDate ? new Date(`${toDate}T23:59:59.999`).getTime() : null;

  const rows = useMemo(() => {
    return jobs
      .filter((j) => j.status === "Completed")
      .filter((j) => {
        const ms = completedMs(j);
        if (fromMs !== null && (ms === null || ms < fromMs)) return false;
        if (toMs !== null && (ms === null || ms > toMs)) return false;
        return true;
      })
      .map((j) => {
        const doc = invByJob[j.id] || estByJob[j.id] || estimates.find((e) => e.id === j.estimateId) || null;
        const p = computeJobProfit(j, doc, byId);
        return { job: j, doc, p, completed: completedMs(j) };
      })
      .filter((r) => r.p.hasData)
      .sort((a, b) => (b.completed || 0) - (a.completed || 0));
  }, [jobs, invByJob, estByJob, estimates, byId, fromMs, toMs]);

  const totals = useMemo(() => {
    const t = { charged: 0, cost: 0, profit: 0, overBudget: 0 };
    rows.forEach(({ p }) => {
      t.charged += p.charged;
      t.cost += p.actualCost;
      t.profit += p.actualProfit;
      if (p.marginDelta < 0) t.overBudget += 1;
    });
    t.margin = t.charged > 0 ? (t.profit / t.charged) * 100 : 0;
    return t;
  }, [rows]);

  const exportCsv = () => {
    const head = ["Job", "Customer", "Completed", "Charged", "Material cost", "Labor (exp)", "Labor (actual)", "Total cost", "Profit", "Actual margin %", "Expected margin %", "Variance %", "Status"];
    const lines = [head.join(",")];
    rows.forEach(({ job, p }) => {
      const cust = (job.customer || "").replace(/,/g, " ");
      lines.push([
        job.id, cust,
        p.actualHours != null ? new Date(job.workCompletedAt || job.end || job.statusChangedAt).toISOString().slice(0, 10) : "",
        p.charged, p.materialCost, p.expHourlyCost + p.flatCost, p.actualHourlyCost + p.flatCost,
        p.actualCost, p.actualProfit, p.actualMargin, p.expectedMargin, p.marginDelta,
        p.marginDelta < 0 ? "Below" : "On/Above",
      ].join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "job-profitability.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  return (
    <div className="space-y-4" data-testid="job-profitability-section">
      {/* KPI strip */}
      <div className="grid grid-cols-4 gap-3">
        <SummaryCard label="Total charged" value={money(totals.charged)} icon={DollarSign} accent="text-blue-600 bg-blue-50" testid="jp-total-charged" />
        <SummaryCard label="Total cost" value={money(totals.cost)} icon={TrendingDown} accent="text-rose-600 bg-rose-50" testid="jp-total-cost" />
        <SummaryCard label="Total profit" value={money(totals.profit)} sub={`${totals.margin.toFixed(1)}% margin`} icon={TrendingUp} accent="text-emerald-600 bg-emerald-50" testid="jp-total-profit" />
        <SummaryCard label="Over budget" value={`${totals.overBudget} / ${rows.length}`} sub="below expected margin" icon={AlertTriangle} accent="text-amber-600 bg-amber-50" testid="jp-over-budget" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-[14px] font-semibold text-slate-900">Job Profitability</h3>
            <p className="text-[12px] text-slate-500">Completed jobs · cost (material + labor) vs amount charged · actual labor from Start → Complete</p>
          </div>
          <button onClick={exportCsv} data-testid="jp-export" className="h-8 px-3 rounded-md border border-slate-200 text-[12.5px] font-semibold text-slate-600 hover:bg-slate-50 flex items-center gap-1.5">
            <Download className="h-3.5 w-3.5" /> Export
          </button>
        </div>
        {rows.length === 0 ? (
          <div className="p-10 text-center text-slate-400 italic text-[13px]" data-testid="jp-empty">No completed jobs with cost data in this range.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="px-4 py-2.5 w-8" />
                  <th className="px-2 py-2.5">Job</th>
                  <th className="px-2 py-2.5">Customer</th>
                  <th className="px-2 py-2.5 text-right">Charged</th>
                  <th className="px-2 py-2.5 text-right">Total cost</th>
                  <th className="px-2 py-2.5 text-right">Profit</th>
                  <th className="px-2 py-2.5 text-right">Margin</th>
                  <th className="px-2 py-2.5 text-right">Expected</th>
                  <th className="px-4 py-2.5 text-right">Vs budget</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ job, p }) => {
                  const below = p.marginDelta < 0;
                  const isOpen = !!expanded[job.id];
                  return (
                    <React.Fragment key={job.id}>
                      <tr className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`jp-row-${job.id}`}>
                        <td className="px-4 py-2.5">
                          <button onClick={() => toggle(job.id)} data-testid={`jp-expand-${job.id}`} className="h-6 w-6 rounded hover:bg-slate-200 flex items-center justify-center text-slate-500">
                            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </button>
                        </td>
                        <td className="px-2 py-2.5 font-semibold text-slate-800">{job.id}</td>
                        <td className="px-2 py-2.5">
                          <button
                            onClick={() => job.customerId && onOpenCustomer?.(job.customerId)}
                            className={job.customerId ? "text-blue-600 hover:underline" : "text-slate-700 cursor-default"}
                          >{job.customer || "—"}</button>
                        </td>
                        <td className="px-2 py-2.5 text-right font-medium text-slate-700">{money(p.charged)}</td>
                        <td className="px-2 py-2.5 text-right text-slate-600">{money(p.actualCost)}</td>
                        <td className={`px-2 py-2.5 text-right font-semibold ${p.actualProfit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{money(p.actualProfit)}</td>
                        <td className="px-2 py-2.5 text-right font-medium text-slate-700">{p.actualMargin.toFixed(1)}%</td>
                        <td className="px-2 py-2.5 text-right text-slate-400">{p.expectedMargin.toFixed(1)}%</td>
                        <td className="px-4 py-2.5 text-right">
                          <span
                            data-testid={`jp-variance-${job.id}`}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                              below ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"
                            }`}
                          >
                            {below ? <TrendingDown className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}
                            {below ? "Below" : "On/Above"} {p.marginDelta > 0 ? "+" : ""}{p.marginDelta.toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-slate-50/40" data-testid={`jp-detail-${job.id}`}>
                          <td />
                          <td colSpan={8} className="px-4 py-3">
                            <div className="grid grid-cols-2 gap-6">
                              {/* Materials */}
                              <div>
                                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Materials used</div>
                                {p.materials.length === 0 ? (
                                  <div className="text-[12.5px] text-slate-400 italic">No materials on this job.</div>
                                ) : (
                                  <table className="w-full text-[12.5px]">
                                    <tbody>
                                      {p.materials.map((m, i) => (
                                        <tr key={i} className="border-b border-slate-100 last:border-0">
                                          <td className="py-1 text-slate-700">{m.name}</td>
                                          <td className="py-1 text-right text-slate-500">{m.qty} × {money(m.unitCost)}</td>
                                          <td className="py-1 text-right font-medium text-slate-700 w-20">{money(m.total)}</td>
                                        </tr>
                                      ))}
                                      <tr>
                                        <td className="py-1 font-semibold text-slate-700">Material cost</td>
                                        <td />
                                        <td className="py-1 text-right font-semibold text-slate-800">{money(p.materialCost)}</td>
                                      </tr>
                                    </tbody>
                                  </table>
                                )}
                              </div>
                              {/* Labor + cost breakdown */}
                              <div>
                                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Labor &amp; cost</div>
                                <table className="w-full text-[12.5px]">
                                  <tbody>
                                    <tr className="border-b border-slate-100">
                                      <td className="py-1 text-slate-600">Expected labor hours</td>
                                      <td className="py-1 text-right text-slate-700">{p.expHours} h</td>
                                    </tr>
                                    <tr className="border-b border-slate-100">
                                      <td className="py-1 text-slate-600">Actual labor hours</td>
                                      <td className={`py-1 text-right font-medium ${p.actualHours != null && p.actualHours > p.expHours ? "text-rose-600" : "text-slate-700"}`}>
                                        {p.actualHours != null ? `${p.actualHours} h` : "— (no clock)"}
                                      </td>
                                    </tr>
                                    <tr className="border-b border-slate-100">
                                      <td className="py-1 text-slate-600">Labor cost (exp → actual)</td>
                                      <td className="py-1 text-right text-slate-700">{money(p.expHourlyCost + p.flatCost)} → {money(p.actualHourlyCost + p.flatCost)}</td>
                                    </tr>
                                    <tr className="border-b border-slate-100">
                                      <td className="py-1 text-slate-600">Expected total cost</td>
                                      <td className="py-1 text-right text-slate-700">{money(p.expectedCost)}</td>
                                    </tr>
                                    <tr>
                                      <td className="py-1 font-semibold text-slate-700">Actual total cost</td>
                                      <td className={`py-1 text-right font-semibold ${p.costVariance > 0 ? "text-rose-600" : "text-slate-800"}`}>
                                        {money(p.actualCost)} {p.costVariance > 0 ? `(+${money(p.costVariance)} over)` : ""}
                                        {p.actualHours == null && (
                                          <span className="ml-1.5 px-1.5 py-0.5 rounded bg-slate-200 text-slate-500 text-[10px] font-medium align-middle" data-testid={`jp-estimated-${job.id}`}>estimated</span>
                                        )}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, sub, icon: Icon, accent, testid }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-400">{label}</span>
        <span className={`h-7 w-7 rounded-md flex items-center justify-center ${accent}`}><Icon className="h-4 w-4" /></span>
      </div>
      <div className="mt-1.5 text-[22px] font-bold text-slate-900">{value}</div>
      {sub && <div className="text-[11.5px] text-slate-400">{sub}</div>}
    </div>
  );
}
